/* eslint-disable @typescript-eslint/naming-convention */
import {
  Filters,
  SearchFilterOperator,
  SearchFilterOperators
} from '@components/show_page/sections/section_search/SearchTypes';

/**
 * Generate filter query string
 * @param filters - The filter object
 * @param searchQuery - Optional search query
 * @param searchFilterOperators - Optional search filter operators
 * @param options - Optional configuration
 * @param options.includePeopleFilter - Whether to include people filter in query (default: false)
 *   - true: Include people filter (for UI display in "Query:" section)
 *   - false: Exclude people filter (for API - uses identity_ids param instead)
 */
const filterLogic = (
  filters: Filters,
  searchQuery?: string,
  searchFilterOperators?: SearchFilterOperator | SearchFilterOperators,
  options?: { includePeopleFilter?: boolean }
): string => {
  const { includePeopleFilter = false } = options || {};
  const filter_aspect_array = filters.orientation || [];
  const filter_availability = filters.availability;
  const filter_comments = filters.comments;
  const filter_created_at = filters.created_at;
  const filter_custom_fields = filters.custom_fields || {};
  const filter_filetypes_array = filters.filetypes || [];
  const filter_people_array = filters.people || [];
  const filter_section = filters.section;
  const filter_sku = filters.sku;
  const filter_tags_array = filters.tags || [];
  const filter_task_assigned_to = filters.task_assigned_to;
  const filter_task_due_date = filters.task_due_date;
  const filter_task_priority = filters.task_priority;
  const filter_task_status = filters.task_status;
  const filter_view_only = filters.view_only;

  let extra_filters = '';
  let queryString = searchQuery ? searchQuery.trim() : '';

  if (filter_sku) {
    extra_filters += filter_sku;
  }

  if (filter_created_at) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND created_at:${filter_created_at}`;
    } else {
      extra_filters = `${extra_filters}created_at:${filter_created_at}`;
    }
  }

  if (filter_availability) {
    if (extra_filters) {
      extra_filters += ' AND ';
    }

    switch (filter_availability) {
      case 'approved':
        extra_filters += 'approved:true';
        break;
      case 'unapproved': case 'pending_approval':
        extra_filters += 'approved:false';
        break;
      case 'draft': case 'unpublished':
        extra_filters += 'unpublished:true';
        break;
      case 'expired':
        extra_filters += 'expired:true';
        break;
      default:
        break;
    }
  }

  if (filter_comments === false || filter_comments === true) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND comments:${filter_comments}`;
    } else {
      extra_filters = `${extra_filters}comments:${filter_comments}`;
    }
  }

  const filter_aspect_array_length = filter_aspect_array.length;
  if (filter_aspect_array_length === 1) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND aspect:${filter_aspect_array[0]}`;
    } else {
      extra_filters = `${extra_filters}aspect:${filter_aspect_array[0]}`;
    }
  } else if (filter_aspect_array_length > 1) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND (`;
    } else {
      extra_filters = `${extra_filters}(`;
    }
    filter_aspect_array.forEach((aspect, i) => {
      extra_filters = `${extra_filters}aspect:${aspect}`;
      if (i < (filter_aspect_array_length - 1)) {
        extra_filters = `${extra_filters} OR `;
      }
    });
    extra_filters = `${extra_filters})`;
  }

  const filter_tags_array_length = filter_tags_array.length;
  if (filter_tags_array_length === 1) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND tags.strict:"${filter_tags_array[0]}"`;
    } else {
      extra_filters = `${extra_filters}tags.strict:"${filter_tags_array[0]}"`;
    }
  } else if (filter_tags_array_length > 1) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND (`;
    } else {
      extra_filters = `${extra_filters}(`;
    }
    filter_tags_array.forEach((tag, i) => {
      extra_filters = `${extra_filters}tags.strict:"${tag}"`;
      if (i < (filter_tags_array_length - 1)) {
        extra_filters = `${extra_filters} ${(searchFilterOperators as SearchFilterOperators)?.tags || SearchFilterOperator.OR} `;
      }
    });
    extra_filters = `${extra_filters})`;
  }

  const filter_filetypes_array_length = filter_filetypes_array.length;
  if (filter_filetypes_array_length === 1) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND filetype.strict:"${filter_filetypes_array[0]}"`;
    } else {
      extra_filters = `${extra_filters}filetype.strict:"${filter_filetypes_array[0]}"`;
    }
  } else if (filter_filetypes_array_length > 1) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND (`;
    } else {
      extra_filters = `${extra_filters}(`;
    }
    filter_filetypes_array.forEach((filetype, i) => {
      extra_filters = `${extra_filters}filetype.strict:"${filetype}"`;
      if (i < (filter_filetypes_array_length - 1)) {
        extra_filters = `${extra_filters} OR `;
      }
    });
    extra_filters = `${extra_filters})`;
  }

  const custom_field_keys = Object.keys(filter_custom_fields);
  const multiValueOperator = (key): string => (
    `" ${(searchFilterOperators as SearchFilterOperators)?.custom_fields?.[key] || SearchFilterOperator.OR} "`
  );
  if (custom_field_keys.length === 1) {
    const custom_field_key = custom_field_keys[0];
    const custom_field_values = `"${filter_custom_fields[custom_field_key].join(multiValueOperator(custom_field_key))}"`;
    if (extra_filters) {
      extra_filters = `${extra_filters} AND custom_fields.${custom_field_key}.strict:(${custom_field_values})`;
    } else {
      extra_filters = `${extra_filters}custom_fields.${custom_field_key}.strict:(${custom_field_values})`;
    }
  } else if (custom_field_keys.length > 1) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND (`;
    } else {
      extra_filters = `${extra_filters}(`;
    }

    custom_field_keys.forEach((custom_field_key, i) => {
      const custom_field_values = `"${filter_custom_fields[custom_field_key].join(multiValueOperator(custom_field_key))}"`;
      extra_filters = `${extra_filters}custom_fields.${custom_field_key}.strict:(${custom_field_values})`;
      if (i < (custom_field_keys.length - 1)) {
        extra_filters = `${extra_filters} ${SearchFilterOperator.AND} `;
      }
    });
    extra_filters = `${extra_filters})`;
  }

  if (filter_section) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND section:${filter_section}`;
    } else {
      extra_filters = `section:${filter_section}`;
    }
  }

  if (filter_task_assigned_to) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND task_assigned_to:${filter_task_assigned_to}`;
    } else {
      extra_filters = `task_assigned_to:${filter_task_assigned_to}`;
    }
  }

  if (filter_task_due_date) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND task_due_date:${filter_task_due_date}`;
    } else {
      extra_filters = `task_due_date:${filter_task_due_date}`;
    }
  }

  if (filter_task_priority) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND task_priority:${filter_task_priority}`;
    } else {
      extra_filters = `task_priority:${filter_task_priority}`;
    }
  }

  if (filter_task_status) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND task_status:${filter_task_status}`;
    } else {
      extra_filters = `task_status:${filter_task_status}`;
    }
  }

  if (filter_view_only) {
    if (extra_filters) {
      extra_filters = `${extra_filters} AND view_only:${filter_view_only}`;
    } else {
      extra_filters = `view_only:${filter_view_only}`;
    }
  }

  // Extract people from searchQuery if it contains people.strict filters
  // This handles the case where user clicks on a face box (which adds people.strict to searchQuery)
  // and then adds another person via advanced filters
  const peopleFromQuery: string[] = [];
  let queryStringWithoutPeople = queryString;

  if (queryString && includePeopleFilter) {
    // Match patterns like: people.strict:("PersonName") or people.strict:("Person1") OR people.strict:("Person2")
    const peopleStrictRegex = /people\.strict:\("([^"]+)"\)/g;
    let match;
    while ((match = peopleStrictRegex.exec(queryString)) !== null) {
      peopleFromQuery.push(match[1]);
    }

    // Remove people.strict filters from queryString
    if (peopleFromQuery.length > 0) {
      // Remove all people.strict patterns
      queryStringWithoutPeople = queryString.replace(/people\.strict:\("([^"]+)"\)/g, '');

      // Clean up operators that are left behind
      const cleanupPatterns: [RegExp, string][] = [
        [/\s+AND\s+AND\s+/g, ' AND '],   // Replace double AND
        [/\s+OR\s+OR\s+/g, ' OR '],       // Replace double OR
        [/\s+AND\s+OR\s+/g, ' OR '],      // Replace AND OR with OR
        [/\s+OR\s+AND\s+/g, ' AND '],     // Replace OR AND with AND
        [/^\s*(?:AND|OR)\s+/i, ''],       // Remove leading operator
        [/\s+(?:AND|OR)\s*$/i, ''],       // Remove trailing operator
        [/\(\s*\)/g, ''],                  // Remove empty parentheses
        [/\(\s*\(/g, '('],                // Clean up nested empty parentheses start
        [/\)\s*\)/g, ')'],                // Clean up nested empty parentheses end
      ];

      queryStringWithoutPeople = cleanupPatterns
        .reduce((str, [pattern, replacement]) => str.replace(pattern, replacement), queryStringWithoutPeople)
        .trim();

      // If queryString becomes empty after removing people filters, set to empty string
      if (!queryStringWithoutPeople || queryStringWithoutPeople === '()' || queryStringWithoutPeople.match(/^[\s()]*$/)) {
        queryStringWithoutPeople = '';
      }
    }
  }

  // Combine people from query and filters.people array
  const allPeople: Array<{ id?: string; name: string } | string> = [];

  // Add people from searchQuery
  peopleFromQuery.forEach((personName) => {
    allPeople.push(personName);
  });

  // Add people from filters.people array
  filter_people_array.forEach((person) => {
    const personName = person?.name || person;
    // Avoid duplicates
    if (!allPeople.some((p) => {
      const pName = typeof p === 'string' ? p : (p?.name || p);
      return pName === personName;
    })) {
      allPeople.push(person);
    }
  });

  // filter_people_array contains objects with {id, name} - use names for query display
  // Only include in query string for UI display (not for API - API uses identity_ids param)
  // Multiple people should use OR operation (assets containing any of the selected people)
  if (includePeopleFilter && allPeople.length > 0) {
    if (allPeople.length === 1) {
      const personName = typeof allPeople[0] === 'string' ? allPeople[0] : (allPeople[0]?.name || allPeople[0]);
      if (extra_filters) {
        extra_filters = `${extra_filters} AND people.strict:("${personName}")`;
      } else {
        extra_filters = `people.strict:("${personName}")`;
      }
    } else {
      if (extra_filters) {
        extra_filters = `${extra_filters} AND (`;
      } else {
        extra_filters = `(`;
      }
      allPeople.forEach((person, i) => {
        const personName = typeof person === 'string' ? person : (person?.name || person);
        extra_filters = `${extra_filters}people.strict:("${personName}")`;
        if (i < (allPeople.length - 1)) {
          // Use OR for multiple people - assets containing any of the selected people
          extra_filters = `${extra_filters} OR `;
        }
      });
      extra_filters = `${extra_filters})`;
    }
  }

  // Combine queryString (without people filters) and extra_filters
  if (!!extra_filters && !!queryStringWithoutPeople) {
    queryString = `(${queryStringWithoutPeople}) AND ${extra_filters}`;
  } else if (extra_filters && !queryStringWithoutPeople) {
    queryString = extra_filters;
  } else if (queryStringWithoutPeople && !extra_filters) {
    queryString = queryStringWithoutPeople;
  } else {
    queryString = '';
  }

  return window.encodeURIComponent(queryString);
};

export default filterLogic;
